import { useCallback, useSyncExternalStore } from 'react';
import type { RevealApi } from 'reveal.js';

interface NetworkBackgroundProps {
  className?: string;
  deck: RevealApi;
}

type NetworkMode = 'full' | 'hidden';

// The network background mode is fully derived from the reveal.js deck's
// current slide, so it is read straight from that external system via
// useSyncExternalStore instead of being duplicated into local state.
const useNetworkMode = (deck: RevealApi): NetworkMode => {
  const subscribe = useCallback(
    (callback: () => void) => {
      if (!deck) return () => {};
      deck.on('slidechanged', callback);
      return () => {
        deck.off('slidechanged', callback);
      };
    },
    [deck]
  );

  const getSnapshot = useCallback((): NetworkMode => {
    const networkMode = deck?.getCurrentSlide()?.dataset.networkBackground;
    return (networkMode as NetworkMode) || 'hidden';
  }, [deck]);

  return useSyncExternalStore(subscribe, getSnapshot);
};

export const NetworkBackground = ({ deck, className = '' }: NetworkBackgroundProps) => {
  const mode = useNetworkMode(deck);

  if (mode === 'hidden') return null;

  return (
    <div className="fixed inset-0">
      <video
        autoPlay
        loop
        muted
        playsInline
        className={`block w-full h-full object-cover ${className}`}
      >
        <source src="/assets/videos/NetworkBackground.webm" type="video/webm" />
      </video>
    </div>
  );
};
